import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  GraduationCap,
  Loader2,
  BookOpen,
  School,
  Users,
  MapPin,
  Calendar,
  Layers,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import Select from "react-select";
import { AnimatePresence, motion } from "framer-motion";

import {
  createClass,
  updateClass,
  getSchoolPartnersByBranch,
} from "../../../api/elearning/classes/classes";
import { getCoursesByCategory } from "../../../api/elearning/courses/courses";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getTeachersByBranch } from "../../../api/hrm/employees/employeeInfo";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCurrentUser } from "../../../utils/auth";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import { startClassFormModalTour } from "./classFormModalTour";
import {
  SCHEDULE_DAY_OPTIONS,
  TIME_SLOT_OPTIONS,
  parseClassTimeValue,
  buildClassTimeFromRows,
} from "../utils/classTimeDisplay";

const emptyScheduleRows = () =>
  SCHEDULE_DAY_OPTIONS.map((d) => ({
    dayCode: d.value,
    label: d.label,
    enabled: false,
    timeSlot: null,
  }));

const makeRandomClassToken = () =>
  Math.random().toString(36).replace(/[^a-z0-9]/gi, "").slice(0, 6).toUpperCase();

const getYearSuffixYY = () => String(new Date().getFullYear()).slice(-2);

const buildClassCodeFromCourse = (courseOption, randomToken) => {
  const token = String(randomToken || "").toUpperCase();
  const yy = getYearSuffixYY();
  const courseCode = String(courseOption?.code || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

  if (!courseCode) return `LOP${yy}${token}`;
  return `${courseCode}${yy}${token}`;
};

/* ================= COMPONENT PHỤ: SECTION ================= */
const Section = ({ icon: Icon, title, subTitle, children, tourId }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4"
    data-tour={tourId}
  >
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">{title}</h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

/* ================= MAIN COMPONENT ================= */
const ClassFormModal = ({
  isOpen,
  onClose,
  onSaved,
  mode = "create",
  editingClass = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [roomName, setRoomName] = useState("");
  const [scheduleRows, setScheduleRows] = useState(emptyScheduleRows);
  const [nameSuffix, setNameSuffix] = useState(makeRandomClassToken());

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [classType, setClassType] = useState(null);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolSignal, setSchoolSignal] = useState({
    matchedSchoolCount: 0,
    branchTotalCount: 0,
    fallbackUsed: false,
  });

  /* ================= LOGIC GIỮ NGUYÊN ================= */
  useEffect(() => {
    if (!isOpen) return;
    getSubjectsLookup().then((data) =>
      setCategoryOptions(
        (Array.isArray(data) ? data : []).map((s) => ({ value: s.id, label: s.name }))
      )
    );
    getAllBranches_Array().then((list) => {
      setBranchOptions(list.map((b) => ({ value: b.id, label: b.name })));
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") resetForm();
    if (mode === "edit" && editingClass) {
      setName(editingClass.name || "");
      setStartDate(editingClass.start_date?.slice(0, 10) || "");
      setEndDate(editingClass.end_date?.slice(0, 10) || "");
      setRoomName(editingClass.room_name || "");
      const parsedTime = parseClassTimeValue(editingClass.class_time);
      setScheduleRows(parsedTime.rows);
    }
  }, [isOpen, mode, editingClass]);

  useEffect(() => {
    if (!editingClass || !categoryOptions.length || mode !== "edit") return;
    const subject = categoryOptions.find((s) => s.value === editingClass.subject_id);
    if (subject) {
      setSelectedCategory(subject);
      getCoursesByCategory(subject.value).then((data) => {
        const opts = (Array.isArray(data) ? data : []).map((c) => ({
          value: c.id,
          label: c.name,
          code: c.code,
        }));
        setCourseOptions(opts);
        setSelectedCourse(opts.find((c) => c.value === editingClass.course_id) || null);
      });
    }
  }, [editingClass, categoryOptions, mode]);

  useEffect(() => {
    if (!editingClass || !branchOptions.length || mode !== "edit") return;
    const branch = branchOptions.find((b) => b.value === editingClass.branch_id);
    setSelectedBranch(branch || null);
    setClassType(editingClass.class_type || "center");
    if (editingClass.teacher_id) {
      setSelectedTeacher({ value: editingClass.teacher_id, label: editingClass.teacher_name || "Giáo viên" });
    }
  }, [editingClass, branchOptions, mode]);

  useEffect(() => {
    if (!editingClass || classType !== "school") return;
    if (editingClass.school_id) {
      setSelectedSchool({
        value: editingClass.school_id,
        label: editingClass.school_name || "Trường học",
        ward: editingClass.school_ward || "",
      });
    }
  }, [editingClass, classType]);

  useEffect(() => {
    if (classType !== "school" || !selectedBranch) {
      setSchoolOptions([]);
      setSelectedSchool(null);
      setSchoolSignal({ matchedSchoolCount: 0, branchTotalCount: 0, fallbackUsed: false });
      return;
    }
    getSchoolPartnersByBranch(selectedBranch.value).then((res) => {
      setSchoolOptions(
        (res.items || []).map((s) => ({
          value: s.id,
          label: s.name,
          ward: s.ward || "",
        }))
      );
      setSchoolSignal({
        matchedSchoolCount: Number(res?.meta?.matched_school_count || 0),
        branchTotalCount: Number(res?.meta?.branch_total_count || 0),
        fallbackUsed: Boolean(res?.meta?.fallback_used),
      });
    });
  }, [classType, selectedBranch]);

  useEffect(() => {
    if (!selectedBranch) {
      setTeacherOptions([]);
      setSelectedTeacher(null);
      return;
    }
    getTeachersByBranch(selectedBranch.value).then((data) =>
      setTeacherOptions((Array.isArray(data) ? data : []).map((t) => ({ value: t.id, label: t.name })))
    );
  }, [selectedBranch]);

  const resetForm = () => {
    const nextSuffix = makeRandomClassToken();
    setNameSuffix(nextSuffix);
    setName(`LOP${getYearSuffixYY()}${nextSuffix}`);
    setStartDate("");
    setEndDate("");
    setRoomName("");
    setScheduleRows(emptyScheduleRows());
    setSelectedTeacher(null); setSelectedCategory(null); setSelectedCourse(null);
    setCourseOptions([]); setSelectedBranch(null); setClassType(null); setSelectedSchool(null);
  };

  useEffect(() => {
    if (mode !== "create") return;
    setName(buildClassCodeFromCourse(selectedCourse, nameSuffix));
  }, [selectedCourse, mode, nameSuffix]);

  const handleSave = async () => {
    if (!selectedBranch) return showErrorToast("Vui lòng chọn Chi nhánh");
    if (!classType) return showErrorToast("Vui lòng chọn Phân loại lớp");
    if (classType === "school" && !selectedSchool) return showErrorToast("Vui lòng chọn Trường học");
    if (!selectedCategory) return showErrorToast("Vui lòng chọn Môn học");
    if (!selectedCourse) return showErrorToast("Vui lòng chọn Khóa học");
    if (!startDate || !endDate) return showErrorToast("Vui lòng chọn ngày học");
    if (endDate < startDate) return showErrorToast("Ngày kết thúc phải sau ngày bắt đầu");
    const activeRows = scheduleRows.filter((r) => r.enabled && r.timeSlot);
    if (!activeRows.length) return showErrorToast("Vui lòng chọn ít nhất một ngày và khung giờ học");

    const classTime = buildClassTimeFromRows(scheduleRows);
    const payload = {
      name, course_id: selectedCourse.value, class_type: classType, branch_id: selectedBranch.value,
      school_id: classType === "school" ? selectedSchool?.value : null,
      start_date: startDate, end_date: endDate, room_name: roomName, class_time: classTime,
      teacher_id: selectedTeacher?.value || null,
    };
    if (mode === "create") { payload.is_active = true; payload.created_by = getCurrentUser()?.id || null; }

    try {
      setLoading(true);
      if (mode === "create") { await createClass(payload); showSuccessToast("Tạo lớp học thành công!"); }
      else { await updateClass(editingClass.id, payload); showSuccessToast("Cập nhật lớp học thành công!"); }
      onSaved?.(); onClose();
    } catch { showErrorToast("Không thể lưu lớp học"); } finally { setLoading(false); }
  };

  const handleQuickTour = () => {
    window.setTimeout(() => startClassFormModalTour(), 80);
  };

  /* ================= RENDER ================= */
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop / Overlay */}
        <motion.div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal Content Container */}
        <motion.div
          className="relative z-10 w-full max-w-4xl"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div
            className="bg-slate-50 rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20"
            data-tour="class-form-modal"
          >
            {/* HEADER */}
            <div
              className="bg-white px-8 py-6 flex justify-between items-center border-b border-gray-100"
              data-tour="class-form-header"
            >
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 bg-red-600 rounded-3xl flex items-center justify-center shadow-xl shadow-red-200">
                  <GraduationCap className="text-white w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 leading-none uppercase tracking-tight">
                    {mode === "create" ? "Tạo lớp học mới" : "Chỉnh sửa thông tin"}
                  </h2>
                  <p className="text-[11px] font-bold text-gray-400 mt-2 uppercase tracking-widest">
                    Điền đầy đủ các thông tin bắt buộc <span className="text-red-500">(*)</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2" data-tour="class-form-guide-btn">
                <button
                  type="button"
                  onClick={handleQuickTour}
                  title="Hướng dẫn nhanh"
                  aria-label="Hướng dẫn nhanh"
                  className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
                >
                  <Sparkles className="h-5 w-5" strokeWidth={2.2} />
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
                    Hướng dẫn nhanh
                  </span>
                </button>
                <button
                  onClick={onClose}
                  className="p-3 bg-gray-50 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-600 border border-transparent hover:border-gray-200"
                >
                  <X size={24} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* BODY: SCROLLABLE */}
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto max-h-[70vh] custom-scrollbar">

              {/* CỘT TRÁI: ĐỊA ĐIỂM & CHƯƠNG TRÌNH */}
              <div className="space-y-8">
                <Section
                  icon={MapPin}
                  title="Địa điểm đào tạo"
                  subTitle="Nơi diễn ra các hoạt động học tập"
                  tourId="class-form-location"
                >
                  <Select
                    styles={customSelectStyles}
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={(b) => { setSelectedBranch(b); setClassType(null); setSelectedSchool(null); }}
                    placeholder="Chọn chi nhánh sở tại *"
                    {...modalSelectPortalProps}
                  />

                  {selectedBranch && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      {[
                        { id: 'center', label: 'Tại trung tâm', icon: '🏛️' },
                        { id: 'school', label: 'Tại trường học', icon: '🏫' }
                      ].map(type => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setClassType(type.id)}
                          className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${classType === type.id
                            ? "border-red-600 bg-red-50 text-red-700 shadow-lg shadow-red-100"
                            : "border-gray-100 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          <span className="text-2xl mb-2">{type.icon}</span>
                          <span className="text-[11px] font-black uppercase tracking-wider">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {classType === "school" && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="overflow-hidden">
                      <div className="mt-2">
                        <Select
                          styles={customerInModalSelectStyles}
                          options={schoolOptions}
                          value={selectedSchool}
                          onChange={setSelectedSchool}
                          placeholder="Chọn đối tác trường học *"
                          formatOptionLabel={formatCustomerOptionLabel}
                          filterOption={filterCustomerOption}
                          {...modalSelectPortalProps}
                        />
                        <p className="mt-1 text-[10px] font-semibold tracking-wide text-gray-500">
                          {`Khớp loại trường: ${schoolSignal.matchedSchoolCount} | Tổng đối tác chi nhánh: ${schoolSignal.branchTotalCount}${
                            schoolSignal.fallbackUsed ? " | Đang dùng fallback dữ liệu" : ""
                          }`}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </Section>

                <Section
                  icon={BookOpen}
                  title="Chương trình học"
                  subTitle="Danh mục môn và khóa học cụ thể"
                  tourId="class-form-program"
                >
                  <div className="space-y-4">
                    <Select
                      styles={customSelectStyles}
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={(c) => {
                        setSelectedCategory(c); setSelectedCourse(null); setCourseOptions([]);
                        if (c) getCoursesByCategory(c.value).then((d) =>
                          setCourseOptions(
                            (Array.isArray(d) ? d : []).map((x) => ({
                              value: x.id,
                              label: x.name,
                              code: x.code,
                            }))
                          )
                        );
                      }}
                      placeholder="Chọn môn học nền tảng *"
                      {...modalSelectPortalProps}
                    />
                    <Select
                      styles={customSelectStyles}
                      options={courseOptions}
                      value={selectedCourse}
                      onChange={setSelectedCourse}
                      placeholder="Chọn khóa học chi tiết *"
                      {...modalSelectPortalProps}
                    />
                  </div>
                </Section>
              </div>

              {/* CỘT PHẢI: CHI TIẾT LỚP & NHÂN SỰ */}
              <div className="space-y-8">
                <Section
                  icon={Layers}
                  title="Thông tin lớp học"
                  subTitle="Định danh và kế hoạch thời gian"
                  tourId="class-form-info"
                >
                  <div className="space-y-4">
                    <div className="relative group">
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value.toUpperCase())}
                        disabled={mode === "create"}
                        className="w-full bg-white border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 outline-none focus:border-red-600 focus:ring-4 focus:ring-red-50 transition-all"
                        placeholder="Tên lớp tự sinh theo khóa học"
                      />
                    </div>
                    <div className="relative group">
                      <input
                        value={roomName}
                        onChange={(e) => setRoomName(e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 outline-none focus:border-red-600 focus:ring-4 focus:ring-red-50 transition-all"
                        placeholder="Số phòng / Tên phòng học"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Ngày bắt đầu</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 focus:border-red-600 outline-none transition-all" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Ngày kết thúc</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                          className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold text-gray-800 focus:border-red-600 outline-none transition-all" />
                      </div>
                    </div>
                  </div>
                </Section>

                <Section
                  icon={Users}
                  title="Nhân sự & Lịch học"
                  subTitle="Giáo viên và khung giờ theo từng ngày"
                  tourId="class-form-staffing"
                >
                  <div className="space-y-4">
                    <Select
                      styles={customSelectStyles}
                      options={teacherOptions}
                      value={selectedTeacher}
                      onChange={setSelectedTeacher}
                      placeholder="Phân công giáo viên chủ nhiệm"
                      {...modalSelectPortalProps}
                    />
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                      <div className="grid grid-cols-[1fr,auto,1.4fr] gap-2 px-3 py-2 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-400">
                        <span>Ngày</span>
                        <span className="text-center">Học</span>
                        <span>Khung giờ</span>
                      </div>
                      {scheduleRows.map((row) => (
                        <div
                          key={row.dayCode}
                          className="grid grid-cols-[1fr,auto,1.4fr] gap-2 px-3 py-2.5 border-t border-gray-50 items-center"
                        >
                          <span className="text-sm font-bold text-gray-700">{row.label}</span>
                          <input
                            type="checkbox"
                            checked={row.enabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setScheduleRows((prev) =>
                                prev.map((r) =>
                                  r.dayCode === row.dayCode
                                    ? {
                                        ...r,
                                        enabled: checked,
                                        timeSlot: checked ? r.timeSlot || "18:00-20:00" : null,
                                      }
                                    : r
                                )
                              );
                            }}
                            className="w-4 h-4 accent-red-600 justify-self-center"
                          />
                          <Select
                            styles={customSelectStyles}
                            options={TIME_SLOT_OPTIONS}
                            value={
                              row.timeSlot
                                ? TIME_SLOT_OPTIONS.find((s) => s.value === row.timeSlot) || null
                                : null
                            }
                            onChange={(opt) =>
                              setScheduleRows((prev) =>
                                prev.map((r) =>
                                  r.dayCode === row.dayCode
                                    ? {
                                        ...r,
                                        enabled: true,
                                        timeSlot: opt?.value || null,
                                      }
                                    : r
                                )
                              )
                            }
                            isDisabled={!row.enabled}
                            placeholder="Chọn giờ"
                            {...modalSelectPortalProps}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 font-medium">
                      Có thể chọn khung giờ khác nhau cho từng ngày trong tuần.
                    </p>
                  </div>
                </Section>
              </div>
            </div>

            {/* FOOTER */}
            <div
              className="bg-white px-8 py-6 flex justify-between items-center border-t border-gray-100"
              data-tour="class-form-actions"
            >
              <button
                onClick={onClose}
                className="px-8 py-3.5 rounded-2xl font-black text-[11px] text-gray-400 uppercase tracking-[0.2em] hover:bg-gray-50 transition-all"
              >
                Hủy bỏ
              </button>

              <button
                onClick={handleSave}
                disabled={loading}
                className="px-10 py-3.5 rounded-2xl bg-red-600 text-white font-black text-[11px] uppercase tracking-[0.2em] flex items-center gap-3 hover:bg-red-700 hover:shadow-2xl hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {loading ? "Đang xử lý..." : "Lưu lớp học"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default ClassFormModal;